import React, { useCallback, useRef } from 'react';
import noop from 'lodash/noop';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import AppSwitcherIcon from '@atlaskit/icon/core/app-switcher';
import CopyIcon from '@atlaskit/icon/core/copy';
import { Box, Text, xcss } from '@atlaskit/primitives';
import {
	AsyncBulkOpsCore,
	BulkOpsCoreWithToolbar,
} from '@atlassian/jira-bulk-operations-async-core';
import type { BulkOpsCoreType } from '@atlassian/jira-bulk-operations-async-core/src/ui';
import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common/src/controllers/use-form-submit-state/types.tsx';
import type { ReferenceLoader } from '@atlassian/jira-bulk-operations-common/src/utils/reference-loader/use-reference-loader/index.tsx';
import type { EntryPointReferenceSubject } from '@atlassian/jira-entry-point';
import { fg } from '@atlassian/jira-feature-gating';
import type { ToolbarAction } from '@atlassian/jira-floating-toolbar/src/ui/toolbar-button';
import { useCloseIssueViewOnBulkOpsEdit } from '../../services/use-close-issue-view';
import { useBacklogDispatch, useBacklogSelector } from '../../state';
import {
	cardsBulkOperationsFailure,
	cardsBulkOperationsRequests,
	cardsBulkOperationsSuccess,
	type CardsBulkOperationsPayload,
} from '../../state/actions/cards/bulk-operations';
import { getIsCompanyManaged } from '../../state/selectors/app';
import { getCards, getSelectedCards, getVisibleCardIds } from '../../state/selectors/cards';
import { getEstimationFieldId } from '../../state/selectors/estimation';
import { canBulkEditIssues } from '../../state/selectors/permissions';
import { getBulkOperationsData } from '../bulk-operations-sidebar/utils';
import { useSelectActions } from './select-actions';

type BulkOperationsLoaderProps = {
	bulkOperationsToolbar: ReferenceLoader; // remove this on bulk_operations_toolbar_milestone_1 cleanup
	bulkOperationsForm: ReferenceLoader;
	onOpenSidebar: () => void;
	onCloseSidebar: () => void;
	bulkopsCoreEntryPointReference?: // remove ? on bulk_operations_toolbar_milestone_1 cleanup
	| EntryPointReferenceSubject<EntryPoint<EntryPointComponent<{}, {}, BulkOpsCoreType, {}>, {}>>
		| undefined;
};

export const useSelectedCards = () => useBacklogSelector(getSelectedCards);
export const useHasBulkEditPermission = () => useBacklogSelector(canBulkEditIssues);
export const useVisibleCardIds = () => useBacklogSelector(getVisibleCardIds);
export const useEstimationFieldId = () => useBacklogSelector(getEstimationFieldId);
export const useIsCMP = () => useBacklogSelector(getIsCompanyManaged);

export const extraToolbarButtons: ToolbarAction[] = [
	{
		type: 'perform',
		icon: CopyIcon,
		name: 'Copy link',
		onClick: noop,
	},
	{
		type: 'popup',
		icon: AppSwitcherIcon,
		name: 'Apps',
		isOpen: false,
		content: () => (
			<Box xcss={wrapperPopupContentStyle}>
				<Text>Example popup content</Text>
			</Box>
		),
	},
];

export const BulkOperationsLoader = ({
	bulkOperationsToolbar,
	bulkOperationsForm,
	onOpenSidebar,
	onCloseSidebar,
	bulkopsCoreEntryPointReference,
}: BulkOperationsLoaderProps) => {
	const selectedCardIds = useSelectedCards();
	const hasBulkEditPermission = useHasBulkEditPermission();
	const cardEntities = useBacklogSelector(getCards);

	const visibleCardIds = useVisibleCardIds();
	const { selectAllVisible, clearAll, clearAllVisible, extraAnalyticsAttributes } =
		useSelectActions();

	const dispatch = useBacklogDispatch();
	const estimationFieldId = useEstimationFieldId();

	const isCMP = useIsCMP();

	const { openIssueView, closeIssueView } = useCloseIssueViewOnBulkOpsEdit();

	const optimisticId = useRef<string | undefined>();

	const onUpdateStart = useCallback(
		(payload: IssuesUpdatePayload[]) => {
			const cardsData: CardsBulkOperationsPayload[] = payload.map(({ issueIds, data }) => {
				const issuesData = getBulkOperationsData({
					data,
					estimationFieldId,
					isCMP,
				});
				return {
					cardIds: issueIds,
					data: issuesData,
				};
			});
			const action = cardsBulkOperationsRequests(cardsData);
			optimisticId.current = action.meta.optimistic.id;
			dispatch(action);
		},
		[dispatch, estimationFieldId, isCMP],
	);

	/*
    This function is a temporary workaround to open the legacy bulk edit page.
    The Bulk Transition doesn't have support to return the issueKeys so this implementation was done here
    and will be removed when we implement Bulk Transition with multiple workflows.
    Project Plan: https://hello.atlassian.net/wiki/spaces/JSDee/pages/3388755384/Bulk+Transition+Project+Plan
    */
	const onLegacyBulkEditRequest = useCallback(
		(issueIds: number[]) => {
			const issueKeys = issueIds.map((id) => cardEntities[String(id)]?.key);
			const url = `/secure/GHBulkChange.jspa?issueKeys=${encodeURIComponent(issueKeys.join(','))}`;

			window.open(url, '_blank');
		},
		[cardEntities],
	);

	const onUpdateError = useCallback(
		(cardIds: number[]) => {
			if (!optimisticId.current) {
				return;
			}
			dispatch(cardsBulkOperationsFailure(optimisticId.current, cardIds));
		},
		[dispatch],
	);

	const onUpdateSuccess = useCallback(
		(cardIds: number[]) => {
			if (!optimisticId.current) {
				return;
			}
			dispatch(cardsBulkOperationsSuccess(optimisticId.current, cardIds));
		},
		[dispatch],
	);

	const onOpenForm = useCallback(() => {
		onOpenSidebar();
		closeIssueView();
	}, [closeIssueView, onOpenSidebar]);

	const onCloseForm = useCallback(() => {
		onCloseSidebar();
		openIssueView();
	}, [openIssueView, onCloseSidebar]);

	const props = {
		bulkOperationsForm,
		onOpenForm,
		onCloseForm,
		onUpdateError,
		onUpdateSuccess,
		onLegacyBulkEditRequest,
		onSelectAll: selectAllVisible,
		selectableIssueIds: visibleCardIds,
		selectedIssueIds: selectedCardIds,
		extraAnalyticsAttributes,
		onClearAll: clearAll,
		onClearAllVisible: clearAllVisible,
		hasBulkEditPermission,
		onUpdateStart,
	};

	if (fg('bulk_operations_toolbar_milestone_1')) {
		return (
			<BulkOpsCoreWithToolbar
				shouldHideToolbar={false}
				bulkopsCoreEntryPointReference={bulkopsCoreEntryPointReference}
				extraToolbarButtons={extraToolbarButtons}
				{...props}
			/>
		);
	}

	return <AsyncBulkOpsCore {...props} bulkOperationsToolbar={bulkOperationsToolbar} />;
};

const wrapperPopupContentStyle = xcss({
	padding: 'space.250',
});
